import type { QueryClient } from "@tanstack/react-query";
import {
  getDeanSessionRevision,
  rememberDeanConversation,
  setDeanSessionScope,
  type StorageLike,
} from "./deanChatLifecycle";

export interface DeanIdentity {
  id: string;
  role?: string;
  ownerUserId?: string | null;
  dealershipId?: string | null;
  impersonatingUserId?: string | null;
  ghostMode?: boolean;
  editAccessApproved?: boolean;
}

export function deanAccountAccess(user: DeanIdentity | null | undefined) {
  const canImpersonate = user?.role === "owner" || user?.role === "admin";
  const impersonating = canImpersonate && !!user?.impersonatingUserId;
  return {
    canImpersonate,
    missingDealership: canImpersonate && !impersonating && !user?.ownerUserId && !user?.dealershipId,
    viewOnly: impersonating && (!user?.editAccessApproved || !!user?.ghostMode),
  };
}

export function canReadDeanConversations(
  session: boolean,
  missingDealership: boolean,
  error?: unknown,
) {
  return session && !missingDealership && !deanMembershipMessage(error);
}

// A missing permission/context disables the composer; it is never a request.
export function deanComposerState(options: {
  session: boolean;
  needsDealership: boolean;
  viewOnly: boolean;
  creating: boolean;
  unresolved: boolean;
  uploading: boolean;
}) {
  const blocked = !options.session || options.needsDealership || options.viewOnly;
  const busy = !blocked && (options.creating || options.unresolved);
  return { busy, disabled: blocked || busy || options.uploading };
}

export function deanIdentityScope(user: DeanIdentity | null | undefined): string | null {
  if (!user?.id) return null;
  return JSON.stringify([
    user.id,
    user.impersonatingUserId ?? user.dealershipId ?? user.ownerUserId ?? user.id,
    user.impersonatingUserId ?? null,
    !!user.ghostMode,
  ]);
}

export function clearDeanQueries(client: QueryClient) {
  const predicate = (query: { queryKey: readonly unknown[] }) =>
    typeof query.queryKey[0] === "string" && query.queryKey[0].startsWith("/api/conversations");
  void client.cancelQueries({ predicate });
  client.removeQueries({ predicate });
}

export function resetDeanSession(client: QueryClient, storage?: StorageLike | null) {
  void client.cancelQueries({ queryKey: ["/api/auth/me"], exact: true });
  setDeanSessionScope(null, storage, true);
  clearDeanQueries(client);
}

// Observe the same auth identity used everywhere in the app, including View as
// dealer. Cancel old requests before dropping cached lists and message bodies.
export function bindDeanSession(client: QueryClient, storage?: StorageLike | null) {
  const sync = (user: DeanIdentity | null | undefined) => {
    if (setDeanSessionScope(deanIdentityScope(user), storage)) clearDeanQueries(client);
  };
  sync(client.getQueryData(["/api/auth/me"]));
  return client.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] !== "/api/auth/me") return;
    if (event.type === "removed") sync(null);
    else if (event.type === "updated" && event.action.type === "success") sync(event.query.state.data as DeanIdentity | null);
    else if (event.type === "updated" && event.action.type === "error"
      && (event.query.state.error as { status?: number } | null)?.status === 401) sync(null);
  });
}

export const DEAN_MEMBERSHIP_MESSAGE =
  "DEAN needs a dealership. Use View as dealer or switch to a dealership account.";

export function deanMembershipMessage(error: unknown): string | null {
  const value = error as { status?: number; message?: string } | null;
  return value?.status === 403 && /dealership membership|membership.*required/i.test(value.message ?? "")
    ? DEAN_MEMBERSHIP_MESSAGE : null;
}

export async function sendDeanConversation(options: {
  activeId: () => number | null;
  create: () => Promise<number>;
  send: (id: number) => Promise<void>;
  discard: (id: number) => void;
}) {
  const revision = getDeanSessionRevision();
  const assertCurrent = () => {
    if (getDeanSessionRevision() !== revision) throw new DOMException("Account changed", "AbortError");
  };
  let id = options.activeId();
  if (id === null) {
    id = await options.create();
    assertCurrent();
  }
  try {
    await options.send(id);
  } catch (error) {
    assertCurrent();
    if ((error as { status?: number }).status !== 404) throw error;
    options.discard(id);
    rememberDeanConversation(null);
    const freshId = await options.create();
    assertCurrent();
    try { await options.send(freshId); }
    catch (retryError) {
      if ((retryError as { status?: number }).status === 404) options.discard(freshId);
      throw retryError; // Never loop on an inaccessible conversation.
    }
  }
}
